import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { downloadInvoicePdf, printInvoicePdf } from '../../utils/invoicePdf'
import * as api from '../../api/index'
import { useRhGuard } from '../../hooks/useRoleGuard'
import { isAdmin } from '../../utils/auth'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import Spinner from '../Spinner/Spinner'
import { formatTND } from '../../utils/formatTND'
import styles from './Coaches.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const formatPeriod = (month, year) => {
  if (!month || !year) return '—'
  return `${MONTHS[month]} ${year}`
}

const statusBadge = (status) => {
  if (status === 'sent') return { cls: styles.badgeOk, text: 'Envoyée' }
  if (status === 'failed') return { cls: styles.badgeErr, text: 'Échec' }
  return { cls: styles.badgePending, text: 'En attente' }
}

const coachToForm = (coach) => ({
  name: coach?.name || '',
  matriculeFiscal: coach?.matriculeFiscal || '',
  address: coach?.address || '',
  email: coach?.email || '',
  rib: coach?.rib || '',
  titre: coach?.titre || 'Coach sportif',
})

const CoachDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useRhGuard()
  const admin = isAdmin()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState(coachToForm(null))
  const [saving, setSaving] = useState(false)
  const limit = 10

  const loadCoach = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await api.fetchCoach(id, { page, limit })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Coach introuvable')
      history.push('/coaches')
    } finally {
      setLoading(false)
    }
  }, [id, page, history])

  useEffect(() => {
    loadCoach()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page])

  const downloadPdf = async (invoiceId, campaignId, invoiceNumber) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    try {
      await downloadInvoicePdf(campaignId, invoiceId, invoiceNumber)
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    }
  }

  const printPdf = async (invoiceId, campaignId) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    try {
      await printInvoicePdf(campaignId, invoiceId)
    } catch {
      openSnackbar('Erreur impression PDF')
    }
  }

  const handleDeleteCoach = async () => {
    setDeleting(true)
    try {
      await api.deleteCoach(id)
      openSnackbarRef.current('Coach supprimé')
      history.push('/coaches')
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  const openEdit = () => {
    if (!data?.coach) return
    setForm(coachToForm(data.coach))
    setEditOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      openSnackbar('Nom requis')
      return
    }
    if (!form.matriculeFiscal.trim()) {
      openSnackbar('Matricule fiscal requis')
      return
    }
    setSaving(true)
    try {
      await api.updateCoach(id, form)
      openSnackbarRef.current('Coach mis à jour')
      setEditOpen(false)
      loadCoach()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur enregistrement')
    } finally {
      setSaving(false)
    }
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  if (!data?.coach) return null

  const { coach, invoices = [], pagination } = data

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => history.push('/coaches')}>
        ← Retour aux coaches
      </button>

      <div className={styles.profileCard}>
        <div className={styles.profileAvatar}>{coach.name?.charAt(0)?.toUpperCase()}</div>
        <div className={styles.profileInfo}>
          <h1>{coach.name}</h1>
          <div className={styles.profileMeta}>
            <span>MF : {coach.matriculeFiscal}</span>
            <span>{coach.email || 'Email non renseigné'}</span>
            <span>RIB : {coach.rib || 'Non renseigné'}</span>
            <span>{coach.titre || 'Coach sportif'}</span>
            <span>{coach.address || 'Adresse non renseignée'}</span>
          </div>
        </div>
        <div className={styles.profileActions}>
          <button
            type="button"
            className={styles.profileEditBtn}
            onClick={openEdit}
          >
            Modifier
          </button>
          {admin && (
            <button
              type="button"
              className={styles.profileDeleteBtn}
              disabled={deleting}
              onClick={() => setConfirmDelete(true)}
            >
              Supprimer
            </button>
          )}
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Factures</span>
          <span className={styles.kpiValue}>{coach.invoiceCount || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Honoraires cumulés</span>
          <span className={styles.kpiValue}>{formatTND(coach.totalHonoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net cumulé</span>
          <span className={styles.kpiValue}>{formatTND(coach.totalNet)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Dernière campagne</span>
          <span className={styles.kpiValue} style={{ fontSize: '1.1rem' }}>
            {coach.periodLabel || '—'}
          </span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Historique des factures</h2>
            <p className={styles.hint}>{pagination?.total || 0} facture(s) pour ce coach</p>
          </div>
        </div>

        {invoices.length === 0 ? (
          <p className={styles.empty}>Aucune facture enregistrée pour ce coach.</p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Période</th>
                    <th>Honoraires</th>
                    <th>Net à payer</th>
                    <th>Statut envoi</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const st = statusBadge(inv.emailStatus)
                    return (
                      <tr key={inv._id}>
                        <td data-label="N° Facture">{inv.invoiceNumber}</td>
                        <td data-label="Période">{formatPeriod(inv.periodMonth, inv.periodYear)}</td>
                        <td data-label="Honoraires">{formatTND(inv.honoraires)} TND</td>
                        <td data-label="Net à payer">{formatTND(inv.netAPayer)} TND</td>
                        <td data-label="Statut envoi"><span className={st.cls}>{st.text}</span></td>
                        <td data-label="Actions">
                          <button
                            type="button"
                            className={styles.tableBtn}
                            onClick={() => downloadPdf(inv._id, inv.campaignId, inv.invoiceNumber)}
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            className={styles.tableBtn}
                            style={{ marginLeft: 8 }}
                            onClick={() => printPdf(inv._id, inv.campaignId)}
                          >
                            Imprimer
                          </button>
                          {inv.campaignId && (
                            <button
                              type="button"
                              className={styles.tableBtn}
                              style={{ marginLeft: 8 }}
                              onClick={() => history.push(`/campagnes/${inv.campaignId}`)}
                            >
                              Campagne
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {pagination?.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {pagination.page} sur {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= pagination.totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {editOpen && (
        <div className={styles.overlay} onClick={() => !saving && setEditOpen(false)} role="presentation">
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="coach-edit-title"
          >
            <h2 id="coach-edit-title">Modifier le coach</h2>
            <p className={styles.modalHint}>
              Les modifications sont appliquées à la fiche et aux factures existantes de ce coach.
            </p>
            <form onSubmit={handleSave}>
              <div className={styles.field}>
                <label htmlFor="coach-name">Nom complet *</label>
                <input
                  id="coach-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Ex. Hassan Ben Farhat"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-mf">Matricule fiscal *</label>
                <input
                  id="coach-mf"
                  value={form.matriculeFiscal}
                  onChange={(e) => setForm({ ...form, matriculeFiscal: e.target.value })}
                  required
                  placeholder="12007658Y/A/P/000"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-email">Email</label>
                <input
                  id="coach-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="coach@california-gym.com"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-rib">RIB</label>
                <input
                  id="coach-rib"
                  value={form.rib}
                  onChange={(e) => setForm({ ...form, rib: e.target.value.replace(/\s/g, '') })}
                  placeholder="20 chiffres"
                  inputMode="numeric"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-titre">Titre / fonction</label>
                <input
                  id="coach-titre"
                  value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  placeholder="Coach sportif"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-address">Adresse</label>
                <textarea
                  id="coach-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Adresse complète"
                  rows={3}
                />
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setEditOpen(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmDelete}
        variant="warning"
        title="Supprimer ce coach ?"
        message={`Vous allez supprimer la fiche de ${coach.name} du référentiel.`}
        detail={
          (coach.invoiceCount || 0) > 0
            ? `Les ${coach.invoiceCount} facture(s) restent dans leurs campagnes. Un prochain import peut recréer la fiche.`
            : 'Un prochain import Excel peut recréer automatiquement la fiche coach.'
        }
        confirmLabel={deleting ? 'Suppression…' : 'Supprimer'}
        cancelLabel="Annuler"
        loading={deleting}
        onConfirm={handleDeleteCoach}
        onCancel={() => !deleting && setConfirmDelete(false)}
      />
    </div>
  )
}

export default CoachDetail
